// Whoop-style score ring. The fill is the visual signal; the score sits in
// the centre as the headline. Used in HeroStrip cards on the dashboard.
//
// Variants colour the ring fill: "ink" (default), "sage" (positive),
// "warn" (concern). The CSS guard on `.score-ring .ring-fg` respects
// prefers-reduced-motion.

import { useEffect, useState } from "react";

export type ScoreRingProps = {
  score: number; // 0-100. NaN/null callers should pass undefined.
  size?: number; // diameter in px, default 110
  strokeWidth?: number; // default 8
  variant?: "ink" | "sage" | "warn";
  /** Optional small sub-label below the score in the centre. */
  centreSub?: string;
};

export function ScoreRing({
  score,
  size = 110,
  strokeWidth = 8,
  variant = "ink",
  centreSub,
}: ScoreRingProps) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(100, score));

  const [offset, setOffset] = useState(circumference);
  useEffect(() => {
    const target = circumference - (clamped / 100) * circumference;
    const id = requestAnimationFrame(() => setOffset(target));
    return () => cancelAnimationFrame(id);
  }, [clamped, circumference]);

  const variantClass = variant === "sage" ? "score-ring s" : variant === "warn" ? "score-ring w" : "score-ring";

  // NOTE: deliberately no `class="ring"` on the <svg>: Tailwind v4 has a
  // `ring` utility that paints a 1px box-shadow around the element, which
  // would draw a black square around every ring on the dashboard. Use a
  // child selector in CSS instead (`.score-ring > svg`).
  return (
    <div className={variantClass} style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        aria-hidden="true"
      >
        <circle
          className="ring-bg"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
        />
        <circle
          className="ring-fg"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <div className="ring-centre">
        <span className="ring-score">{Math.round(clamped)}</span>
        {centreSub ? <span className="ring-sub">{centreSub}</span> : null}
      </div>
    </div>
  );
}
